import { Loading, Scrollable } from '@affine/component';
import { fitReadonlyEdgelessViewport } from '@affine/core/modules/streamlain/edge-infrastructure/readonly-edge-runtime';
import type { DocMode } from '@blocksuite/affine/model';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { Store } from '@blocksuite/affine/store';
import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback } from 'react';

import { PureEditorModeSwitch } from '../../../blocksuite/block-suite-mode-switch';
import { AffineErrorBoundary } from '../affine-error-boundary';
import {
  ReadonlyDocRenderSurface,
  type ReadonlyDocRenderSurfaceHandle,
} from './readonly-doc-render-surface';
import * as styles from './styles.css';

export interface HistoryEdgelessViewport {
  centerX: number;
  centerY: number;
  zoom: number;
}

export interface ReadonlyDocSnapshotPreviewProps {
  snapshotPage?: Store;
  mode: DocMode;
  onModeChange: (mode: DocMode | 'fullscreen' | 'elster' | 'publish') => void;
  title: ReactNode;
  headerEnd?: ReactNode;
  className?: string;
  style?: CSSProperties;
  initialEdgelessViewport?: HistoryEdgelessViewport | null;
  onEdgelessViewportChange?: (viewport: HistoryEdgelessViewport) => void;
  unavailableReason?: ReactNode;
  /** Omit history-specific chrome when embedding the canonical renderer. */
  hideHeader?: boolean;
  /** Fit the complete historical canvas once after the readonly editor mounts. */
  fitEdgelessOnReady?: boolean;
}

export const ReadonlyDocSnapshotPreview = ({
  snapshotPage,
  mode,
  onModeChange,
  title,
  headerEnd,
  className,
  style,
  initialEdgelessViewport,
  onEdgelessViewportChange,
  unavailableReason,
  hideHeader = false,
  fitEdgelessOnReady = false,
}: ReadonlyDocSnapshotPreviewProps) => {
  const handleEditorReady = useCallback(
    (editor: ReadonlyDocRenderSurfaceHandle) => {
      if (mode !== 'edgeless') {
        return;
      }

      const gfx = editor.host?.std.getOptional(GfxControllerIdentifier);
      const viewport = gfx?.viewport;
      if (!viewport) {
        return;
      }

      if (fitEdgelessOnReady) {
        requestAnimationFrame(() => fitReadonlyEdgelessViewport(gfx));
      }

      if (initialEdgelessViewport) {
        viewport.setViewport(
          initialEdgelessViewport.zoom,
          [initialEdgelessViewport.centerX, initialEdgelessViewport.centerY],
          false
        );
      }

      onEdgelessViewportChange?.({
        centerX: viewport.centerX,
        centerY: viewport.centerY,
        zoom: viewport.zoom,
      });

      const subscription = viewport.viewportUpdated.subscribe(update => {
        onEdgelessViewportChange?.({
          centerX: update.center[0],
          centerY: update.center[1],
          zoom: update.zoom,
        });
      });

      return () => subscription.unsubscribe();
    },
    [
      fitEdgelessOnReady,
      initialEdgelessViewport,
      mode,
      onEdgelessViewportChange,
    ]
  );

  return (
    <div className={clsx(styles.previewContent, className)} style={style}>
      {!hideHeader ? (
        <div className={styles.previewHeader}>
          <PureEditorModeSwitch
            mode={mode}
            setMode={onModeChange}
            hideFullscreen
          />
          <div className={styles.previewHeaderTitle}>{title}</div>
          <div className={styles.previewHeaderTimestamp}>{headerEnd}</div>
        </div>
      ) : null}

      {snapshotPage ? (
        <AffineErrorBoundary>
          <Scrollable.Root className={styles.previewScrollable}>
            <Scrollable.Viewport className="affine-page-viewport">
              <ReadonlyDocRenderSurface
                className={styles.editor}
                mode={mode}
                onReady={handleEditorReady}
                page={snapshotPage}
              />
            </Scrollable.Viewport>
            <Scrollable.Scrollbar />
          </Scrollable.Root>
        </AffineErrorBoundary>
      ) : unavailableReason ? (
        <div className={styles.previewUnavailableContainer} role="status">
          {unavailableReason}
        </div>
      ) : (
        <div className={styles.loadingContainer}>
          <Loading size={24} />
        </div>
      )}
    </div>
  );
};
